import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ComposerPromptEditorHandle } from "../ComposerPromptEditor";
import { FIRST_ACCOUNT_SETUP_STARTER_PROMPT_STORAGE_KEY } from "../../lib/firstAccountSetupStarterPrompt";
import { readStoredValue, removeStoredValue } from "../../lib/storage";
import {
  resolveFirstAccountSetupSeed,
  resolveInitialFirstAccountSetupPrompt,
  resolveQaSeedPrompt,
} from "./newThreadPromptSeed";

/**
 * The prompt being typed, the caret in it, and the two things that seed both.
 *
 * Cursor is tracked alongside the text because a programmatic change has to say
 * where the caret lands — a seeded prompt should leave it at the end, not at
 * the start, which is why every seed writes both and why the editor ref lives
 * here too rather than beside the JSX that consumes it.
 *
 * Every decision the two seeding effects make is answered by the pure resolvers
 * in `newThreadPromptSeed`; what is left here is the part that cannot be pure —
 * reading `window`, writing state, and moving focus.
 */
export function useSeededComposerPrompt(input?: {
  readonly prompt: string;
  readonly onPromptChange: (prompt: string) => void;
}): {
  readonly editorRef: React.RefObject<ComposerPromptEditorHandle | null>;
  readonly prompt: string;
  readonly setPrompt: (next: string) => void;
  readonly composerCursor: number;
  readonly onPromptChange: (nextPrompt: string, nextCursor: number) => void;
} {
  const editorRef = useRef<ComposerPromptEditorHandle | null>(null);
  const controlledInputRef = useRef(input);
  controlledInputRef.current = input;
  const initialPrompt = useMemo(readInitialPromptFromWindow, []);
  const [localPrompt, setLocalPrompt] = useState(initialPrompt ?? "");
  const prompt = input?.prompt ?? localPrompt;
  const setPrompt = useCallback((next: string) => {
    const controlledInput = controlledInputRef.current;
    if (controlledInput) controlledInput.onPromptChange(next);
    else setLocalPrompt(next);
  }, []);
  const [composerCursor, setComposerCursor] = useState(initialPrompt?.length ?? 0);

  const onPromptChange = useCallback(
    (nextPrompt: string, nextCursor: number) => {
      setPrompt(nextPrompt);
      setComposerCursor(nextCursor);
    },
    [setPrompt],
  );

  const seedAtEnd = useCallback(
    (next: string) => {
      setPrompt(next);
      setComposerCursor(next.length);
    },
    [setPrompt],
  );

  useEffect(() => {
    const seedPrompt = resolveQaSeedPrompt({
      hostname: window.location.hostname,
      search: window.location.search,
    });
    if (!seedPrompt) return;

    seedAtEnd(seedPrompt);
    // Focus is deferred a frame: the editor has not rendered the seeded text
    // yet, so asking for a caret at its end in this tick asks for an offset
    // that does not exist.
    requestAnimationFrame(() => {
      editorRef.current?.focusAt(seedPrompt.length);
    });
  }, [seedAtEnd]);

  useEffect(() => {
    const seed = resolveFirstAccountSetupSeed({
      search: window.location.search,
      storedPrompt: readStoredValue(FIRST_ACCOUNT_SETUP_STARTER_PROMPT_STORAGE_KEY),
      initialPrompt,
    });
    if (!seed.prompt) return;

    removeStoredValue(FIRST_ACCOUNT_SETUP_STARTER_PROMPT_STORAGE_KEY);
    if (seed.clearSearchParam) clearFirstAccountSetupSearchParam();
    // Guarded so this cannot fight the user: the effect re-runs on every
    // keystroke (it reads `prompt`), and without the check it would rewrite
    // whatever they had just typed back to the starter prompt.
    if (prompt !== seed.prompt) seedAtEnd(seed.prompt);
  }, [initialPrompt, prompt, seedAtEnd]);

  return { editorRef, prompt, setPrompt, composerCursor, onPromptChange };
}

/** The setup prompt this page opened with, read once before the first render. */
function readInitialPromptFromWindow(): string | null {
  if (typeof window === "undefined") return null;
  return resolveInitialFirstAccountSetupPrompt({
    search: window.location.search,
    storedPrompt: readStoredValue(FIRST_ACCOUNT_SETUP_STARTER_PROMPT_STORAGE_KEY),
  });
}

/** Drop `?firstAccountSetupPrompt=1` without adding a history entry. */
function clearFirstAccountSetupSearchParam(): void {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete("firstAccountSetupPrompt");
  const nextSearch = searchParams.toString();
  window.history.replaceState(
    window.history.state,
    "",
    nextSearch ? `${window.location.pathname}?${nextSearch}` : window.location.pathname,
  );
}
